import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import ConfirmModal, { ConfirmVariant } from "./ConfirmModal";
import { useNotifier } from "../../Core/Notifier";

export interface ConfirmOptions {
  title?: string;
  message?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmVariant;
  /**
   * Optional action to run while the modal shows a loading state. If it
   * resolves, the modal closes and (optionally) a success toast is shown; if it
   * throws, the modal stays open and an error toast is shown so the user can
   * retry or cancel. When omitted, confirm() simply resolves to `true` and the
   * caller runs the action itself.
   */
  onConfirm?: () => Promise<any> | any;
  /** Success toast shown after `onConfirm` resolves. */
  successMessage?: string;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

const DEFAULTS: Required<Pick<ConfirmOptions, "title" | "message" | "confirmText" | "cancelText" | "variant">> = {
  title: "Confirm Action",
  message: "Are you sure you want to proceed?",
  confirmText: "Yes, Proceed",
  cancelText: "Cancel",
  variant: "default",
};

/**
 * Global confirmation gate for destructive actions. Mount once near the app
 * root; call `useConfirm()` anywhere to require an explicit confirmation before
 * a delete / remove / unlink / disable / reset action runs.
 */
export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions>({});
  const resolver = useRef<((value: boolean) => void) | null>(null);

  const settle = useCallback((value: boolean) => {
    resolver.current?.(value);
    resolver.current = null;
  }, []);

  const confirm = useCallback<ConfirmFn>((opts) => {
    setOptions(opts);
    setLoading(false);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const handleCancel = useCallback(() => {
    if (loading) return;
    setOpen(false);
    settle(false);
  }, [loading, settle]);

  const handleConfirm = useCallback(async () => {
    if (!options.onConfirm) {
      setOpen(false);
      settle(true);
      return;
    }
    setLoading(true);
    try {
      await options.onConfirm();
      if (options.successMessage) showMessage(options.successMessage);
      setLoading(false);
      setOpen(false);
      settle(true);
    } catch (err: any) {
      // Keep the modal open so the user can retry or cancel.
      setLoading(false);
      showErrorMessage(err?.data?.message || err?.message || "Action failed. Please try again.");
    }
  }, [options, settle, showMessage, showErrorMessage]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmModal
        open={open}
        title={options.title ?? DEFAULTS.title}
        message={options.message ?? DEFAULTS.message}
        confirmText={options.confirmText ?? DEFAULTS.confirmText}
        cancelText={options.cancelText ?? DEFAULTS.cancelText}
        variant={options.variant ?? DEFAULTS.variant}
        loading={loading}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmContext.Provider>
  );
};

/**
 * Returns an imperative `confirm(options)` that resolves to `true` when the
 * user confirms. Pass `onConfirm` to run the action inside the modal with a
 * loading state, or await the boolean and run the action yourself.
 */
export const useConfirm = (): ConfirmFn => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used within a <ConfirmProvider>");
  }
  return ctx;
};

export default ConfirmProvider;
